import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

export interface RelatedLink {
  title: string;
  desc: string;
  to: string;
  tag?: string;
}

interface RelatedServicesProps {
  heading?: string;
  subheading?: string;
  links: RelatedLink[];
  /** "dark" for dark bg sections (default), "light" for light bg sections */
  variant?: "dark" | "light";
}

const RelatedServices = ({
  heading = "Continue Exploring",
  subheading = "Related Services & Resources",
  links,
  variant = "dark",
}: RelatedServicesProps) => {
  const isDark = variant === "dark";

  return (
    <section className={isDark ? "bg-foreground py-14 md:py-20" : "bg-background py-14 md:py-20"}>
      <div className="max-w-[1200px] mx-auto px-5 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <p className={`text-[10px] font-semibold tracking-[.18em] uppercase mb-2 ${isDark ? "text-primary" : "text-primary"}`}>
            {subheading}
          </p>
          <h3 className={`font-display text-[clamp(22px,3.5vw,34px)] font-semibold leading-[1.12] ${isDark ? "text-primary-foreground" : "text-foreground"}`}>
            {heading}
          </h3>
        </motion.div>

        <div className={`grid grid-cols-1 ${links.length <= 2 ? "md:grid-cols-2 gap-8" : "sm:grid-cols-2 lg:grid-cols-3 gap-3"}`}>
          {links.map((link, i) => (
            <motion.div
              key={link.to}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06, duration: 0.45 }}
            >
              <Link
                to={link.to}
                className={`group block rounded-2xl transition-all duration-300 h-full border ${
                  links.length <= 2 ? "p-6 md:p-8" : "p-6 md:p-7"
                } ${
                  isDark
                    ? "bg-primary-foreground/[.04] border-primary-foreground/10 hover:bg-primary-foreground/[.06] hover:border-primary-foreground/15"
                    : "border-border hover:bg-muted/50 hover:border-border/80"
                }`}
              >
                {link.tag && (
                  <span className="inline-block text-[9px] font-bold tracking-[.12em] uppercase text-emerald-accent mb-3">
                    {link.tag}
                  </span>
                )}
                <h4 className={`font-display text-base md:text-lg font-semibold mb-3 leading-snug group-hover:text-primary transition-colors ${
                  isDark ? "text-primary-foreground" : "text-foreground"
                }`}>
                  {link.title}
                </h4>
                <p className={`text-sm font-light leading-relaxed mb-4 ${
                  isDark ? "text-primary-foreground/70" : "text-muted-foreground"
                }`}>
                  {link.desc}
                </p>
                <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-primary group-hover:text-[hsl(var(--gold-300))] transition-colors">
                  Explore <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default RelatedServices;
